import { FileUploadStatus, FileWithStatus } from '@/types/upload';
import { getCleanPath } from '@/utils/path';
import { getFileValidationError, isValidFile, UPLOAD_FILES_MAX_QUEUE_SIZE } from '@/utils/validation';
import { useState } from 'react';
import { FileRejection, FileWithPath, useDropzone } from 'react-dropzone';

type FileRejectionWithPath = FileRejection & {
  file: FileWithPath;
};

export const shouldSkipFolderUploadFile = (filePath: string): boolean => {
  const pathParts = getCleanPath(filePath, true).split('/').filter(Boolean);
  // USD geometry conversion does not need texture assets. Skipping folders
  // named Texture/Textures avoids uploading huge, unrelated image trees.
  return pathParts.slice(0, -1).some(part => /^textures?$/i.test(part));
};

export function useFileDrop({
  onFilesAdded,
  folderId,
}: {
  onFilesAdded: (valid: FileWithStatus[], invalid: FileWithStatus[], folderId?: string) => void;
  folderId?: string;
}) {
  const [resetInputKey, setResetInputKey] = useState(0);

  const onDrop = (acceptedFiles: FileWithPath[], fileRejections: FileRejectionWithPath[]) => {
    const valid: FileWithStatus[] = [];
    const invalid: FileWithStatus[] = [];

    for (const file of acceptedFiles) {
      const filePath = getCleanPath(file.path, true) || file.name;
      if (shouldSkipFolderUploadFile(filePath)) {
        continue;
      }

      const isFileValid = isValidFile(file);

      const fileData: FileWithStatus = {
        id: file.name,
        file,
        name: file.name,
        size: file.size,
        path: filePath,
        status: {
          state: isFileValid ? FileUploadStatus.PENDING : FileUploadStatus.FAILED,
          message: isFileValid ? undefined : getFileValidationError(file),
        },
      };

      if (isFileValid) {
        valid.push(fileData);
      } else {
        invalid.push(fileData);
      }
    }

    for (const { file, errors } of fileRejections) {
      const filePath = getCleanPath(file.path, true) || file.name;
      if (shouldSkipFolderUploadFile(filePath)) {
        continue;
      }

      invalid.push({
        id: file.name,
        name: file.name,
        size: file.size,
        path: filePath,
        file,
        status: {
          state: FileUploadStatus.FAILED,
          code: errors[0]?.code,
          message: errors[0]?.message,
        },
      });
    }
    onFilesAdded(valid, invalid, folderId);
    setResetInputKey(previousKey => previousKey + 1);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: UPLOAD_FILES_MAX_QUEUE_SIZE,
    useFsAccessApi: false,
    noClick: true,
    noKeyboard: true,
    preventDropOnDocument: true,
    multiple: true,
  });

  return {
    dropzoneProps: getRootProps(),
    fileInputProps: {
      ...getInputProps(),
      key: resetInputKey,
    },
    isDragActive,
  };
}
